import { Plus, Trash2 } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { FEATURE_MODULES } from "@/lib/tenant-theme";

/** Feature toggle grid (reference "Feature Toggles") + custom labels
 * editor (reference "Custom Fields and Labels"). Both persist into
 * featureFlags (modules / labels) in Auth config. */
export function FeatureToggles({ featureConfig, onChange }) {
  const modules = featureConfig.modules || {};
  const toggle = (id, v) => onChange({ ...featureConfig, modules: { ...modules, [id]: v } });
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="feature-toggles">
      {FEATURE_MODULES.map((m) => (
        <div key={m.id} className="flex items-start justify-between rounded-md border border-border p-3">
          <div className="pr-2">
            <p className="text-sm font-medium">{m.label}</p>
            <p className="text-xs text-muted-foreground">{m.description}</p>
          </div>
          <Switch
            checked={!!modules[m.id]}
            onCheckedChange={(v) => toggle(m.id, v)}
            data-testid={`feature-${m.id}`}
            aria-label={m.label}
          />
        </div>
      ))}
    </div>
  );
}

const DEFAULT_LABEL_ROWS = [
  { key: "job", defaultLabel: "Job" },
  { key: "candidate", defaultLabel: "Candidate" },
  { key: "company", defaultLabel: "Company" },
  { key: "department", defaultLabel: "Department" },
  { key: "application", defaultLabel: "Application" },
];

export function CustomLabels({ featureConfig, onChange }) {
  const labels = featureConfig.labels || {};
  const extraKeys = Object.keys(labels).filter((k) => !DEFAULT_LABEL_ROWS.some((r) => r.key === k));
  const set = (key, value) => onChange({ ...featureConfig, labels: { ...labels, [key]: value } });
  const removeKey = (key) => {
    const next = { ...labels };
    delete next[key];
    onChange({ ...featureConfig, labels: next });
  };
  const addCustom = () => {
    let i = 1;
    while (labels[`custom_${i}`] !== undefined || extraKeys.includes(`custom_${i}`)) i += 1;
    set(`custom_${i}`, "");
  };
  return (
    <div data-testid="custom-labels">
      <div className="overflow-hidden rounded-md border border-border">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-muted text-left text-muted-foreground">
              <th className="px-3 py-2 font-medium">Default label</th>
              <th className="px-3 py-2 font-medium">Your label</th>
              <th className="w-10 px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {DEFAULT_LABEL_ROWS.map((row) => (
              <tr key={row.key} className="border-b last:border-0">
                <td className="px-3 py-2 text-muted-foreground">{row.defaultLabel}</td>
                <td className="px-3 py-2">
                  <Input className="h-8" data-testid={`label-${row.key}`} placeholder={row.defaultLabel}
                    value={labels[row.key] || ""} onChange={(e) => set(row.key, e.target.value)} />
                </td>
                <td />
              </tr>
            ))}
            {extraKeys.map((key) => (
              <tr key={key} className="border-b last:border-0">
                <td className="px-3 py-2 text-muted-foreground">{key}</td>
                <td className="px-3 py-2">
                  <Input className="h-8" value={labels[key] || ""} onChange={(e) => set(key, e.target.value)} />
                </td>
                <td className="px-3 py-2">
                  <button type="button" aria-label={`Remove ${key}`} onClick={() => removeKey(key)} className="text-muted-foreground hover:text-destructive">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Button type="button" variant="outline" size="sm" className="mt-3 gap-1.5" onClick={addCustom} data-testid="add-custom-field-btn">
        <Plus className="h-4 w-4" /> Add field
      </Button>
      <div className="sr-only"><Label>Custom field label editor</Label></div>
    </div>
  );
}
